import { ActionTree } from 'vuex'

import api from '@/apiClient'

import { QrCode, QrCodeResourcePayload } from '@/models'

import { assertToken, mergeById } from '@/utils'

import { RootState } from '../state'

import {
  ADD_QRCODE,
  INCREMENT_QRCODES_QUERY_INDEX,
  LOADED_ALL_QRCODES,
  RESET_LOADED_ALL_QRCODES,
  RESET_QRCODES_QUERY_INDEX,
  UPDATE_QRCODES,
  UPDATE_QRCODES_TOTAL
} from './mutations'

import { QrCodesState } from './state'

const actions: ActionTree<QrCodesState, RootState> = {
  /**
   * Adds a new QrCode to the state.
   *
   * @param qrCode QrCode to be added.
   */
  addQrCode ({ commit, state }, qrCode: QrCode) {
    commit(UPDATE_QRCODES_TOTAL, state.total + 1)

    commit(ADD_QRCODE, qrCode)
  },

  /**
   * Creates a QrCode.
   *
   * It rejects if the user is not logged in.
   *
   * @param payload Payload to create the QrCode.
   */
  async createQrCode ({ dispatch, rootState, rootGetters }, payload: QrCodeResourcePayload) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const qrCode = await api.menu.qrCodes.createQrCode(tokenV2, providerId, businessUnit.id, payload)

    await dispatch('addQrCode', qrCode)

    return qrCode
  },

  /**
   * Loads one QrCode.
   *
   * It rejects if the user is not logged in.
   *
   * @param id QrCode id to be loaded.
   */
  async loadQrCode ({ commit, dispatch, getters, rootState, rootGetters, state }, id: number) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const qrCode = await api.menu.qrCodes.fetchQrCode(tokenV2, providerId, businessUnit.id, id)

    if (getters.qrCodeById(id) === null) {
      await dispatch('addQrCode', qrCode)
    } else {
      const qrCodes = mergeById(state.elements, [qrCode])
      commit(UPDATE_QRCODES, qrCodes)
    }
  },

  /**
   * Loads a page of QrCodes.
   *
   * It rejects if the user is not logged in.
   *
   * When a previously loaded page changes, it desynchronizes and will load
   * an empty page after the last page, which will reset the query index.
   */
  async loadQrCodePage ({ commit, rootState, rootGetters, state }) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const page = await api.menu.qrCodes.fetchQrCodePage(
      tokenV2,
      providerId,
      businessUnit.id,
      state.query
    )

    if (state.total !== page.total) {
      commit(UPDATE_QRCODES_TOTAL, page.total)
    }

    if (page.elements.length > 0) {
      const qrCodes = mergeById(state.elements, page.elements)

      commit(UPDATE_QRCODES, qrCodes)

      if (qrCodes.length === page.total) {
        commit(LOADED_ALL_QRCODES)
      }

      commit(INCREMENT_QRCODES_QUERY_INDEX, page.elements.length)
    } else {
      commit(RESET_QRCODES_QUERY_INDEX)

      if (page.total === 0 && state.elements.length > 0) {
        commit(UPDATE_QRCODES, [])
      }
    }
  },

  /**
   * Updates a QrCode.
   *
   * It rejects if the user is not logged in.
   *
   * @param id QrCode id to be updated.
   * @param payload Payload to update it.
   */
  async updateQrCode (
    { commit, rootState, rootGetters, state },
    { id, payload }: { id: number; payload: QrCodeResourcePayload }
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const qrCode = await api.menu.qrCodes.updateQrCode(tokenV2, providerId, businessUnit.id, id, payload)

    const qrCodes = mergeById(state.elements, [qrCode])

    commit(UPDATE_QRCODES, qrCodes)
  },

  /**
   * Clears the QrCodesState.
   */
  logout ({ commit }) {
    commit(UPDATE_QRCODES, [])
    commit(UPDATE_QRCODES_TOTAL, 0)
    commit(RESET_LOADED_ALL_QRCODES)
    commit(RESET_QRCODES_QUERY_INDEX)
  }
}

export default actions
